import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import {COORDINATE_SYSTEMS} from '@src/constants/coordinateSystems.js';
import {
  AREA_UNITS,
  DISTANCE_UNITS,
  MEASUREMENT_SYSTEMS,
  UNIT_SYMBOLS,
} from '@src/constants/measureUnits.ts';
import useSettingsStore from '@src/stores/useSettingsStore.js';
import formatNumber from '@src/utils/formatNumber.js';
import React from 'react';
import Setting from './Setting.jsx';

const formatMeasurementExample = (value, unit) =>
  `${formatNumber(value)} ${UNIT_SYMBOLS[unit]}`;

const Header = props => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      marginBlockStart: 1,
      marginInline: 2,
    }}
    {...props}
  />
);

const coordinateSystemOptions = [
  {
    description: 'Decimal degrees',
    example: '37.77° N, 122.41° W',
    value: COORDINATE_SYSTEMS.dd,
  },
  {
    description: 'Degrees, Minutes, Seconds',
    example: '37° 46′ 24″ N 122° 24′ 40″ W',
    value: COORDINATE_SYSTEMS.dms,
  },
  {
    description: 'Military Grid Reference System',
    example: '10SGJ 068 446',
    value: COORDINATE_SYSTEMS.mgrs,
  },
];

const measurementSystemOptions = [
  {
    description: 'Metric',
    value: MEASUREMENT_SYSTEMS.metric,
  },
  {
    description: 'United States Customary (USC)',
    value: MEASUREMENT_SYSTEMS.usc,
  },
];

const distanceUnitOptions = {
  [MEASUREMENT_SYSTEMS.metric]: [
    {
      description: 'Meters',
      example: formatMeasurementExample(1000, DISTANCE_UNITS.metric.m),
      value: DISTANCE_UNITS.metric.m,
    },
    {
      description: 'Kilometers',
      example: formatMeasurementExample(1, DISTANCE_UNITS.metric.km),
      value: DISTANCE_UNITS.metric.km,
    },
  ],
  [MEASUREMENT_SYSTEMS.usc]: [
    {
      description: 'Feet',
      example: formatMeasurementExample(5280, DISTANCE_UNITS.usc.ft),
      value: DISTANCE_UNITS.usc.ft,
    },
    {
      description: 'Miles',
      example: formatMeasurementExample(1, DISTANCE_UNITS.usc.mi),
      value: DISTANCE_UNITS.usc.mi,
    },
    {
      description: 'Nautical Miles',
      example: formatMeasurementExample(0.87, DISTANCE_UNITS.usc.nmi),
      value: DISTANCE_UNITS.usc.nmi,
    },
  ],
};

const areaUnitOptions = {
  [MEASUREMENT_SYSTEMS.metric]: [
    {
      description: 'Square Meters',
      example: formatMeasurementExample(1000000, AREA_UNITS.metric.m2),
      value: AREA_UNITS.metric.m2,
    },
    {
      description: 'Square Kilometers',
      example: formatMeasurementExample(1, AREA_UNITS.metric.km2),
      value: AREA_UNITS.metric.km2,
    },
    {
      description: 'Hectares',
      example: formatMeasurementExample(100, AREA_UNITS.metric.ha),
      value: AREA_UNITS.metric.ha,
    },
  ],
  [MEASUREMENT_SYSTEMS.usc]: [
    {
      description: 'Square Feet',
      example: formatMeasurementExample(27880000, AREA_UNITS.usc.ft2),
      value: AREA_UNITS.usc.ft2,
    },
    {
      description: 'Square Miles',
      example: formatMeasurementExample(1, AREA_UNITS.usc.mi2),
      value: AREA_UNITS.usc.mi2,
    },
    {
      description: 'Square Nautical Miles',
      example: formatMeasurementExample(0.75, AREA_UNITS.usc.nmi2),
      value: AREA_UNITS.usc.nmi2,
    },
    {
      description: 'Acres',
      example: formatMeasurementExample(640, AREA_UNITS.usc.ac),
      value: AREA_UNITS.usc.ac,
    },
  ],
};

const Settings = () => {
  const measurementSystem = useSettingsStore(state => state.measurement.system);
  const coordinateSystem = useSettingsStore(state => state.coordinateSystem);
  const setCoordinateSystem = useSettingsStore(
    state => state.setCoordinateSystem,
  );
  const setMeasurementSystem = useSettingsStore(
    state => state.setMeasurementSystem,
  );
  const {distanceUnit, areaUnit} = useSettingsStore(state => state.measurement);
  const setAreaUnit = useSettingsStore(state => state.setAreaUnit);
  const setDistanceUnit = useSettingsStore(state => state.setDistanceUnit);

  return (
    <List sx={{overflow: 'auto', height: '100%'}}>
      <Header color="inherit">Coordinate System</Header>
      {coordinateSystemOptions.map(option => (
        <Setting
          key={option.value}
          {...option}
          onChange={setCoordinateSystem}
          selected={coordinateSystem === option.value}
        />
      ))}
      <Divider component="li" sx={{marginBlock: 1}} />
      <Header>Measurement System</Header>
      {measurementSystemOptions.map(option => (
        <Setting
          key={option.value}
          {...option}
          onChange={setMeasurementSystem}
          selected={measurementSystem === option.value}
        />
      ))}

      <Header>Distance</Header>
      {distanceUnitOptions[measurementSystem].map(option => (
        <Setting
          key={option.value}
          {...option}
          onChange={setDistanceUnit}
          selected={distanceUnit === option.value}
        />
      ))}
      <Header>Area</Header>
      {areaUnitOptions[measurementSystem].map(option => (
        <Setting
          key={option.value}
          {...option}
          onChange={setAreaUnit}
          selected={areaUnit === option.value}
        />
      ))}
    </List>
  );
};

export default Settings;
